import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type SortValue = string | number | null | undefined;
export type SortDirection = 'ascending' | 'descending';

/** Keep the supplied order (or `initial`'s) until a header is selected; never mutate query-cache arrays. */
export function useTableSort<T, K extends string>(
  rows: readonly T[],
  columns: Record<K, (row: T) => SortValue>,
  initial: { key: NoInfer<K>; direction: SortDirection } | null = null,
) {
  const { i18n } = useTranslation();
  const [sort, setSort] = useState<{ key: K; direction: SortDirection } | null>(initial);
  const collator = new Intl.Collator(i18n.language, { numeric: true, sensitivity: 'base' });
  const sorted = sort ? [...rows].sort((a, b) => {
    const left = columns[sort.key](a);
    const right = columns[sort.key](b);
    // Missing values stay last in both directions.
    if (left == null) return right == null ? 0 : 1;
    if (right == null) return -1;
    const result = typeof left === 'number' && typeof right === 'number' ? left - right : collator.compare(String(left), String(right));
    return sort.direction === 'ascending' ? result : -result;
  }) : rows;

  const sortProps = (key: K) => ({
    direction: sort?.key === key ? sort.direction : undefined,
    onSort: () => setSort((current) => ({
      key,
      direction: current?.key === key && current.direction === 'ascending' ? 'descending' : 'ascending',
    })),
  });

  return { sorted, sortProps };
}
